import { ChangeEvent, Dispatch, SetStateAction, MouseEvent, useState, useEffect } from "react"
import { SpecifyColumnMetadataType } from "../GenerateDatasetModal"
import { ReactComponent as DeleteIcon } from '../../../../assets/Icons/adornments/Close.svg';
import { ReactComponent as PlusIcon } from '../../../../assets/Icons/adornments/Plus.svg';
import ConditionalSelectField from "../../../../components/Form/ConditionSelectField";
import SelectField from "../../../../components/Form/SelectFields";
import InputField from "../../../../components/Form/InputField";
import Buttons from "../../../../components/Buttons/Buttons";
import AccessGate from "../../../../components/Auth/AccessGate";
import { ac_access } from "../../../../access/predefinedConditions";


const dataTypeOptions = [
    { value: '', label: 'Unspecified' },
    { value: 'single-class-label', label: 'Single Class Label' },
    { value: 'continuous-value', label: 'Continuous Value' }
];

export default function SpecifyColumnMetadata({
    fieldOptions,
    columnMetadataForm,
    setColumnMetadataForm,
    showSelect,
    isSDF,
}: {
    fieldOptions: { value: string, label: string }[],
    columnMetadataForm: SpecifyColumnMetadataType,
    setColumnMetadataForm: Dispatch<SetStateAction<SpecifyColumnMetadataType>>,
    showSelect: boolean,
    isSDF: boolean,
}) {
    const [ options, setOptions ] = useState<{ value: string, label: string }[]>([]);

    useEffect(() => {
        if (fieldOptions.length > 0) {
            setOptions([{ value: '', label: 'Unspecified' }].concat(fieldOptions));
        }
    }, [fieldOptions]);

    const handleSelectColumn = (e: any, name: string, index?: number) => {
        const value = e.value as string;

        const field = columnMetadataForm[name as keyof SpecifyColumnMetadataType];

        setColumnMetadataForm({
            ...columnMetadataForm,
            [name]: Array.isArray(field) && index !== undefined ? field.map((f,i) => {
                if (i === index) {
                    return {...f, assignedColumn: value}
                } else {
                    return {...f}
                }
            }) : {
                ...field,
                assignedColumn: value,
            }
        });
    }

    const handleSelectDatatype = (e: any, name: string, index?: number) => {
        const value = e.value as 'single-class-label' | 'continuous-value' | '';

        setColumnMetadataForm({
            ...columnMetadataForm,
            activityColumns: columnMetadataForm.activityColumns.map((c, i) => {
                if (i === index) {
                    return {
                        ...c,
                        data_type: value,
                    }
                } else {
                    return {...c}
                }
            })
        })
    }

    const handleDescription = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index?: number) => {
        const value = e.target.value;
        const name = e.target.name as keyof SpecifyColumnMetadataType;

        const field = columnMetadataForm[name];

        setColumnMetadataForm({
            ...columnMetadataForm,
            [name]: Array.isArray(field) && index !== undefined ? field.map((f,i) => {
                if (i === index) {
                    return {...f, description: value}
                } else {
                    return {...f}
                }
            }) : {
                ...field,
                description: value,
            }
        });
    }

    const addColumn = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        const id = e.currentTarget.id;

        if (id.includes('activity')) {
            setColumnMetadataForm({
                ...columnMetadataForm,
                activityColumns: columnMetadataForm.activityColumns.concat({
                    assignedColumn: '',
                    description: '',
                    data_type: ''
                })
            })
        } else {
            setColumnMetadataForm({
                ...columnMetadataForm,
                other: columnMetadataForm.other.concat({
                    assignedColumn: '',
                    description: ''
                })
            })
        }
    }

    const removeColumn = (e: MouseEvent<HTMLButtonElement>, index: number) => {
        e.preventDefault();
        const id = e.currentTarget.id;

        if (id.includes('activity')) {
            setColumnMetadataForm({
                ...columnMetadataForm,
                activityColumns: columnMetadataForm.activityColumns.filter((c, i) => i !== index)
            })
        } else {
            setColumnMetadataForm({
                ...columnMetadataForm,
                other: columnMetadataForm.other.filter((c,i) => i !== index)
            })
        }
    }


    return (
        <>
            { !isSDF && // Don't show section to choose an associated structure column
            // if this is an SDF file -- we'll automatically set the molblock to the
            // structure column on the backend
                <div className="flex flex-col gap-6 justify-start items-start">
                    <p className="font-semibold text-default">Structure Column</p>
                    <div className="w-full flex flex-col justify-start items-start gap-4">
                        <ConditionalSelectField label='Associated Column' showSelect={showSelect}>
                            <SelectField 
                                label="Associated Column"
                                id="structure"
                                name="structure"
                                value={columnMetadataForm.structure.assignedColumn ? options.find(opt => opt.value === columnMetadataForm.structure.assignedColumn)! : null}
                                options={options}
                                isRequired={true}
                                isCreatable={false}
                                isMulti={false}
                                handleChange={handleSelectColumn}
                                menuPortalTarget={document.getElementById('generate-ds-popup')}
                            />
                        </ConditionalSelectField>
                        <InputField 
                            label="Column Description"
                            type='textarea'
                            id='structure'
                            name='structure'
                            value={columnMetadataForm.structure.description}
                            isRequired={false}
                            handleChange={handleDescription}
                        />
                    </div>
                </div>
            }

            <AccessGate
                or={ac_access}
            >
                <div className="flex flex-col gap-6 justify-start items-start">
                    <p className="font-semibold text-default">Activity Columns</p>
                    {columnMetadataForm.activityColumns.map((activityColumn, i) => (
                        <div key={i} className="flex justify-start items-start gap-4 w-full pb-6 border-b border-light-outline">
                            {i > 0 && (
                                <button id='remove-activity-column' onClick={(e:any) => removeColumn(e, i)} className="w-[24px] text-outline hover:text-primary transition-all"><DeleteIcon /></button>
                            )}
                            <div className="flex flex-col justify-start items-stretch gap-4 w-full grow">
                                <div className="flex justify-between items-end gap-4">
                                    <div className="w-1/2">
                                        <ConditionalSelectField label='Associated Column' showSelect={showSelect}>
                                            <SelectField 
                                                label="Associated Column"
                                                id="activityColumns"
                                                name="activityColumns"
                                                value={activityColumn.assignedColumn ? options.find(opt => opt.value === activityColumn.assignedColumn)! : null}
                                                options={options}
                                                isRequired={false}
                                                isCreatable={false}
                                                isMulti={false}
                                                handleChange={handleSelectColumn}
                                                index={i}
                                                menuPortalTarget={document.getElementById('generate-ds-popup')}
                                            />
                                        </ConditionalSelectField>
                                    </div>
                                    <div className="w-1/2">
                                        <ConditionalSelectField label='Data Type' showSelect={showSelect}>
                                            <SelectField 
                                                label="Data Type"
                                                id="activityColumns"
                                                name="activityColumns"
                                                value={activityColumn.data_type ? dataTypeOptions.find(opt => opt.value === activityColumn.data_type)! : null}
                                                options={dataTypeOptions}
                                                isRequired={false}
                                                isCreatable={false}
                                                isMulti={false}
                                                handleChange={handleSelectDatatype}
                                                index={i}
                                                menuPortalTarget={document.getElementById('generate-ds-popup')}
                                            />
                                        </ConditionalSelectField>
                                    </div>
                                </div>
                                <div className="w-full">
                                    <InputField 
                                        label="Column Description"
                                        type='textarea'
                                        id='activityColumns'
                                        name='activityColumns'
                                        value={activityColumn.description}
                                        isRequired={false}
                                        index={i}
                                        handleChange={handleDescription}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                    <Buttons
                        id='add-new-column-activity-btn'
                        type='secondary'
                        styling="solid"
                        label='Add Activity Column'
                        startingAdornment={<PlusIcon/>}
                        action={addColumn}
                    />
                </div>
            </AccessGate>


            <div className="flex flex-col gap-6 justify-start items-start">
                <p className="font-semibold text-default">Chemical Name Column</p>
                <div className="w-full flex flex-col justify-start items-start gap-4">
                    <ConditionalSelectField label='Associated Column' showSelect={showSelect}>
                        <SelectField 
                            label="Associated Column"
                            id="chemicalName"
                            name="chemicalName"
                            value={columnMetadataForm.chemicalName.assignedColumn ? options.find(opt => opt.value === columnMetadataForm.chemicalName.assignedColumn)! : null}
                            options={options}
                            isRequired={false}
                            isCreatable={false}
                            isMulti={false}
                            handleChange={handleSelectColumn}
                            menuPortalTarget={document.getElementById('generate-ds-popup')}
                        />
                    </ConditionalSelectField>
                    <InputField 
                        label="Column Description"
                        type='textarea'
                        id='chemicalName'
                        name='chemicalName'
                        value={columnMetadataForm.chemicalName.description}
                        isRequired={false}
                        handleChange={handleDescription}
                    />
                </div>
            </div>

            <div className="flex flex-col gap-6 justify-start items-start">
                <p className="font-semibold text-default">Record ID Columns</p>
                <div className="w-full flex flex-col justify-start items-start gap-4">
                    <ConditionalSelectField label='Associated Column' showSelect={showSelect}>
                        <SelectField 
                            label="Associated Column"
                            id="recordId"
                            name="recordId"
                            value={columnMetadataForm.recordId.assignedColumn ? options.find(opt => opt.value === columnMetadataForm.recordId.assignedColumn)! : null}
                            options={options}
                            isRequired={false}
                            isCreatable={false}
                            isMulti={false}
                            handleChange={handleSelectColumn}
                            menuPortalTarget={document.getElementById('generate-ds-popup')}
                        />
                    </ConditionalSelectField>
                    <InputField 
                        label="Column Description"
                        type='textarea'
                        id='recordId'
                        name='recordId'
                        value={columnMetadataForm.recordId.description}
                        isRequired={false}
                        handleChange={handleDescription}
                    />
                </div>
            </div>

            <div className="flex flex-col gap-6 justify-start items-start">
                <p className="font-semibold text-default">Other Columns</p>
                {columnMetadataForm.other.map((other, i) => (
                    <div key={i} className="flex justify-start items-start gap-4 w-full pb-6 border-b border-light-outline">
                        {i > 0 && (
                            <button id='remove-other-column' onClick={(e:any) => removeColumn(e, i)} className="w-[24px] text-outline hover:text-primary transition-all"><DeleteIcon /></button>
                        )}
                        <div className="flex flex-col justify-start items-stretch gap-4 w-full grow">
                            <ConditionalSelectField label='Associated Column' showSelect={showSelect}>
                                <SelectField 
                                    label="Associated Column"
                                    id="other"
                                    name="other"
                                    value={other.assignedColumn ? options.find(opt => opt.value === other.assignedColumn)! : null}
                                    options={options}
                                    isRequired={false}
                                    isCreatable={false}
                                    isMulti={false}
                                    handleChange={handleSelectColumn}
                                    index={i}
                                    menuPortalTarget={document.getElementById('generate-ds-popup')}
                                />
                            </ConditionalSelectField>
                            <InputField 
                                label="Column Description"
                                type='textarea'
                                id='other'
                                name='other'
                                value={other.description}
                                isRequired={false}
                                index={i}
                                handleChange={handleDescription}
                            />
                        </div>
                    </div>
                ))}
                <Buttons
                    id='add-new-column-other-btn'
                    type='secondary'
                    styling="solid"
                    label='Add Other Column'
                    startingAdornment={<PlusIcon/>}
                    action={addColumn}
                />
            </div>
        </>
    )
}